import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../firebase/config';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [adminInfo, setAdminInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFirstSetup, setIsFirstSetup] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Récupérer les infos admin (claims)
        try {
          const getAdminInfo = httpsCallable(functions, 'getAdminInfo');
          const result = await getAdminInfo();

          if (result.data?.isAdmin) {
            setUser(firebaseUser);
            setAdminInfo(result.data);
          } else {
            // Utilisateur connecté mais pas admin → déconnexion
            await signOut(auth);
            setUser(null);
            setAdminInfo(null);
          }
        } catch (error) {
          console.error('Error getting admin info:', error);
          // Si erreur de permission, l'utilisateur n'est pas admin
          await signOut(auth);
          setUser(null);
          setAdminInfo(null);
        }
      } else {
        setUser(null);
        setAdminInfo(null);
      }
      setLoading(false);
    });

    // Vérifier si c'est le premier setup
    checkFirstSetup();

    return () => unsubscribe();
  }, []);

  const checkFirstSetup = async () => {
    try {
      // Utiliser la Cloud Function publique (pas besoin d'auth)
      const checkSetupFn = httpsCallable(functions, 'checkFirstSetup');
      const result = await checkSetupFn();
      setIsFirstSetup(result.data?.isFirstSetup || false);
    } catch (error) {
      console.error('Error checking first setup:', error);
      // En cas d'erreur, ne pas bloquer - afficher le login normal
      setIsFirstSetup(false);
    }
  };

  const login = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // Vérifier les claims admin
      const getAdminInfo = httpsCallable(functions, 'getAdminInfo');
      const result = await getAdminInfo();

      if (!result.data?.isAdmin) {
        await signOut(auth);
        return { success: false, error: 'Accès non autorisé. Contactez un administrateur.' };
      }

      setAdminInfo(result.data);
      return { success: true, role: result.data.role };
    } catch (error) {
      console.error('Login error:', error);

      if (error.code === 'auth/invalid-credential' || error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        return { success: false, error: 'Email ou mot de passe incorrect' };
      }
      if (error.code === 'auth/too-many-requests') {
        return { success: false, error: 'Trop de tentatives. Réessayez plus tard.' };
      }
      if (error.code === 'auth/invalid-email') {
        return { success: false, error: 'Format d\'email invalide' };
      }

      return { success: false, error: error.message || 'Erreur de connexion' };
    }
  };

  const setupFirstAdmin = async (email, password) => {
    try {
      let userCredential;

      // Stratégie: essayer de se connecter d'abord (cas où le compte existe déjà)
      try {
        userCredential = await signInWithEmailAndPassword(auth, email, password);
        console.log('Compte existant trouvé, ajout des claims admin...');
      } catch (signInError) {
        // Si le compte n'existe pas, le créer
        if (signInError.code === 'auth/user-not-found' || signInError.code === 'auth/invalid-credential') {
          console.log('Création du nouveau compte admin...');
          userCredential = await createUserWithEmailAndPassword(auth, email, password);
        } else if (signInError.code === 'auth/wrong-password') {
          return { success: false, error: 'Ce compte existe mais le mot de passe est incorrect' };
        } else {
          throw signInError;
        }
      }

      // Définir comme super_admin via Cloud Function
      const setAdminClaim = httpsCallable(functions, 'setAdminClaim');
      await setAdminClaim({
        email: email,
        uid: userCredential.user.uid,
        role: 'super_admin'
      });

      // Forcer le refresh du token pour obtenir les claims
      await userCredential.user.getIdToken(true);

      // Récupérer les infos admin
      const getAdminInfo = httpsCallable(functions, 'getAdminInfo');
      const result = await getAdminInfo();
      setAdminInfo(result.data);

      // Marquer que le setup est terminé (ne plus afficher l'écran de config)
      setIsFirstSetup(false);

      return { success: true };
    } catch (error) {
      console.error('Setup error:', error);

      if (error.code === 'auth/email-already-in-use') {
        return { success: false, error: 'Cet email est déjà utilisé. Essayez de vous connecter.' };
      }
      if (error.code === 'auth/weak-password') {
        return { success: false, error: 'Mot de passe trop faible (min 6 caractères)' };
      }
      if (error.code === 'auth/invalid-email') {
        return { success: false, error: 'Format d\'email invalide' };
      }

      return { success: false, error: error.message || 'Erreur de configuration' };
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    setAdminInfo(null);
    // Clear mode sessions
    localStorage.removeItem('orders_mode_session');
    localStorage.removeItem('orders_mode_expiry');
    localStorage.removeItem('delivery_mode_session');
    localStorage.removeItem('delivery_mode_expiry');
  };

  const refreshAdminInfo = async () => {
    try {
      const getAdminInfo = httpsCallable(functions, 'getAdminInfo');
      const result = await getAdminInfo();
      setAdminInfo(result.data);
      return result.data;
    } catch (error) {
      console.error('Error refreshing admin info:', error);
      return null;
    }
  };

  const value = {
    user,
    adminInfo,
    isAuthenticated: !!user && !!adminInfo?.isAdmin,
    isAdmin: adminInfo?.role === 'admin' || adminInfo?.role === 'super_admin',
    isSuperAdmin: adminInfo?.role === 'super_admin',
    isManager: adminInfo?.role === 'manager' || adminInfo?.role === 'admin' || adminInfo?.role === 'super_admin',
    role: adminInfo?.role,
    loading,
    isFirstSetup,
    login,
    logout,
    setupFirstAdmin,
    refreshAdminInfo
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin w-10 h-10 border-4 border-red-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-500 text-sm">Chargement...</p>
        </div>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
